/**
 * useTeachingSession — owns the session code, the session row, the photo list,
 * and autosave wiring for the Teaching Facilitation flow.
 *
 * Design notes:
 * - Text fields debounce at DEBOUNCE_MS to avoid one-request-per-keystroke.
 * - Photo add/remove/tag mutations fire immediately.
 * - On network failure, the pending patch is kept in state so a manual Save /
 *   Retry button can re-send. We don't do an infinite background retry loop —
 *   teachers need to know something's wrong.
 *
 * The hook stays presentation-agnostic; components decide how to render
 * syncState, errors, etc.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addPhoto,
  createSession,
  deletePhoto,
  loadSession,
  updatePhoto,
  updateSession,
  type PhotoPatch,
  type PhotoWithUrl,
  type SessionPatch,
  type TeachingPhotoRow,
  type TeachingSessionRow,
} from '../lib/teachingSession';
import {
  captureVideoFrameAsJpeg,
  resizeImageBlob,
  type ResizeOptions,
} from '../lib/imageResize';
import { normalizeCode } from '../lib/sessionCode';

const DEBOUNCE_MS = 500;

export type SyncState = 'idle' | 'saving' | 'offline' | 'loading';

export interface UseTeachingSessionResult {
  // Identity
  code: string | null;
  session: TeachingSessionRow | null;
  photos: PhotoWithUrl[];

  // Lifecycle
  loading: boolean;
  loadError: string | null;
  syncState: SyncState;
  lastError: string | null;

  // Session ops
  startNew: (initial?: SessionPatch) => Promise<string>;
  resume: (code: string) => Promise<boolean>;
  clear: () => void;
  patch: (p: SessionPatch) => void;

  // Photo ops
  addPhotoFromVideo: (video: HTMLVideoElement, meta?: PhotoPatch) => Promise<PhotoWithUrl>;
  addPhotoFromBlob: (blob: Blob, meta?: PhotoPatch) => Promise<PhotoWithUrl>;
  patchPhoto: (photoId: string, patch: PhotoPatch) => void;
  removePhoto: (photo: TeachingPhotoRow) => Promise<void>;
}

export function useTeachingSession(resizeOptions?: ResizeOptions): UseTeachingSessionResult {
  const [code, setCode] = useState<string | null>(null);
  const [session, setSession] = useState<TeachingSessionRow | null>(null);
  const [photos, setPhotos] = useState<PhotoWithUrl[]>([]);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [lastError, setLastError] = useState<string | null>(null);

  // Pending session-patch debounce
  const pendingSessionPatchRef = useRef<SessionPatch | null>(null);
  const sessionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pending per-photo patches (key = photoId), debounced together
  const pendingPhotoPatchRef = useRef<Map<string, PhotoPatch>>(new Map());
  const photoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flushes
  const flushSessionPatch = useCallback(async () => {
    const pending = pendingSessionPatchRef.current;
    const currentCode = code;
    if (!pending || !currentCode) return;
    pendingSessionPatchRef.current = null;
    setSyncState('saving');
    try {
      const updated = await updateSession(currentCode, pending);
      setSession(updated);
      setSyncState('idle');
      setLastError(null);
    } catch (err) {
      // Put the patch back so a retry picks it up, and mark offline.
      pendingSessionPatchRef.current = {
        ...pending,
        ...(pendingSessionPatchRef.current ?? {}),
      };
      setSyncState('offline');
      setLastError(err instanceof Error ? err.message : String(err));
    }
  }, [code]);

  const flushPhotoPatches = useCallback(async () => {
    const pending = pendingPhotoPatchRef.current;
    if (pending.size === 0) return;
    const entries = Array.from(pending.entries());
    pendingPhotoPatchRef.current = new Map();
    setSyncState('saving');
    try {
      const updated = await Promise.all(
        entries.map(async ([photoId, patch]) => {
          const result = await updatePhoto(photoId, patch);
          return result;
        }),
      );
      setPhotos((prev) => {
        const byId = new Map(prev.map((p) => [p.id, p] as const));
        updated.forEach((p) => byId.set(p.id, p));
        return Array.from(byId.values()).sort(
          (a, b) =>
            a.sort_index - b.sort_index ||
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
      });
      setSyncState('idle');
      setLastError(null);
    } catch (err) {
      // Merge back failed patches
      entries.forEach(([id, patch]) => {
        const existing = pendingPhotoPatchRef.current.get(id);
        pendingPhotoPatchRef.current.set(id, { ...patch, ...existing });
      });
      setSyncState('offline');
      setLastError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (sessionDebounceRef.current) clearTimeout(sessionDebounceRef.current);
      if (photoDebounceRef.current) clearTimeout(photoDebounceRef.current);
    };
  }, []);

  // ── Session ops ──────────────────────────────────────────────────────────

  const startNew = useCallback(async (initial: SessionPatch = {}): Promise<string> => {
    setLoading(true);
    setLoadError(null);
    try {
      const row = await createSession(initial);
      setCode(row.code);
      setSession(row);
      setPhotos([]);
      return row.code;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setLoadError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resume = useCallback(async (rawCode: string): Promise<boolean> => {
    const normalized = normalizeCode(rawCode);
    setLoading(true);
    setLoadError(null);
    try {
      const result = await loadSession(normalized);
      if (!result) {
        setLoadError('Session not found or expired');
        return false;
      }
      setCode(result.session.code);
      setSession(result.session);
      setPhotos(result.photos);
      return true;
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    if (sessionDebounceRef.current) clearTimeout(sessionDebounceRef.current);
    if (photoDebounceRef.current) clearTimeout(photoDebounceRef.current);
    pendingSessionPatchRef.current = null;
    pendingPhotoPatchRef.current = new Map();
    setCode(null);
    setSession(null);
    setPhotos([]);
    setSyncState('idle');
    setLastError(null);
    setLoadError(null);
  }, []);

  const patch = useCallback(
    (p: SessionPatch) => {
      if (!code) return;
      // Optimistic merge on the local session so the UI reflects the change now.
      setSession((prev) => (prev ? { ...prev, ...p } : prev));
      pendingSessionPatchRef.current = {
        ...(pendingSessionPatchRef.current ?? {}),
        ...p,
      };
      if (sessionDebounceRef.current) clearTimeout(sessionDebounceRef.current);
      sessionDebounceRef.current = setTimeout(flushSessionPatch, DEBOUNCE_MS);
    },
    [code, flushSessionPatch],
  );

  // ── Photo ops ────────────────────────────────────────────────────────────

  const addPhotoCommon = useCallback(
    async (blob: Blob, meta: PhotoPatch = {}): Promise<PhotoWithUrl> => {
      if (!code) throw new Error('Cannot add photo without an active session');
      setSyncState('saving');
      try {
        const inserted = await addPhoto(code, blob, meta);
        setPhotos((prev) => [...prev, inserted]);
        setSyncState('idle');
        setLastError(null);
        return inserted;
      } catch (err) {
        setSyncState('offline');
        const msg = err instanceof Error ? err.message : String(err);
        setLastError(msg);
        throw err;
      }
    },
    [code],
  );

  const addPhotoFromVideo = useCallback(
    async (video: HTMLVideoElement, meta?: PhotoPatch) => {
      const blob = await captureVideoFrameAsJpeg(video, resizeOptions);
      return addPhotoCommon(blob, meta);
    },
    [addPhotoCommon, resizeOptions],
  );

  const addPhotoFromBlob = useCallback(
    async (raw: Blob, meta?: PhotoPatch) => {
      const downsized = await resizeImageBlob(raw, resizeOptions);
      return addPhotoCommon(downsized, meta);
    },
    [addPhotoCommon, resizeOptions],
  );

  const patchPhoto = useCallback(
    (photoId: string, photoPatch: PhotoPatch) => {
      // Optimistic local merge first so UI typing feels instant
      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, ...photoPatch } : p)),
      );
      const existing = pendingPhotoPatchRef.current.get(photoId) ?? {};
      pendingPhotoPatchRef.current.set(photoId, { ...existing, ...photoPatch });
      if (photoDebounceRef.current) clearTimeout(photoDebounceRef.current);
      photoDebounceRef.current = setTimeout(flushPhotoPatches, DEBOUNCE_MS);
    },
    [flushPhotoPatches],
  );

  const removePhoto = useCallback(async (photo: TeachingPhotoRow) => {
    setSyncState('saving');
    try {
      await deletePhoto(photo);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setSyncState('idle');
      setLastError(null);
    } catch (err) {
      setSyncState('offline');
      setLastError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, []);

  return useMemo(
    () => ({
      code,
      session,
      photos,
      loading,
      loadError,
      syncState,
      lastError,
      startNew,
      resume,
      clear,
      patch,
      addPhotoFromVideo,
      addPhotoFromBlob,
      patchPhoto,
      removePhoto,
    }),
    [
      code,
      session,
      photos,
      loading,
      loadError,
      syncState,
      lastError,
      startNew,
      resume,
      clear,
      patch,
      addPhotoFromVideo,
      addPhotoFromBlob,
      patchPhoto,
      removePhoto,
    ],
  );
}
